import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import InputBase from '@mui/material/InputBase';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import { styled as muiStyled } from '@mui/material/styles';
import propTypes from 'prop-types';
import Checkbox from '@mui/material/Checkbox';
import Slider from '@mui/material/Slider';
import BrainLogo from '../Assets/brain.png';

export const StyledButton = styled.button`
    background: #2e69fe 0% 0% no-repeat padding-box;
    box-shadow: 0px 3px 3px #00000061;
    border-radius: 4px;
    opacity: 1;
    color: #fafbf7;
    padding: 12px;
    font-weight: bold;
    border: unset;
    max-height: 50px;
    max-width: 300px;
    min-width: 50px;
    :hover {
        background-color: #ff8560;
    }
    :disabled {
        background-color: gray;
    }
`;

const Label = styled.div`
    color: white;
    font-weight: bold;
    padding-bottom: 5px;
`;

const StyledTextFieldContainer = styled.div`
    .styled-input {
        border: 1px solid black;
        width: 100%;
        height: 50px;
        padding-left: 12px;
    }
    .label {
        color: white;
    }
`;
export const StyledTextField = ({ value, onChange, placeholder, label }) => {
    return (
        <StyledTextFieldContainer>
            <Label>{label}</Label>
            <input
                className="styled-input"
                type="text"
                value={value}
                onChange={onChange}
                placeholder={placeholder}
            />
        </StyledTextFieldContainer>
    );
};

StyledTextField.propTypes = {
    placeholder: propTypes.string,
    value: propTypes.oneOfType([propTypes.string, propTypes.number]),
    onChange: propTypes.func,
    label: propTypes.string,
};

StyledTextField.defaultProps = {
    placeholder: 'placeholder',
    label: 'label',
    value: '',
    onChange: () => {},
};

const BrainLoaderContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    flex-direction: column;
`;

const pulseAnimation = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
  }
`;

const BrainImgContainer = styled.div`
    max-width: 300px;
`;
const BrainImg = styled.img`
    object-fit: contain;
    width: 100%;
    animation: ${pulseAnimation} 2s infinite;
`;

const BrainLabel = styled.div`
    font-size: 28px;
    margin-top: 15px;
`;

export const BrainLoader = ({ label, width }) => {
    return (
        <BrainLoaderContainer>
            <BrainImgContainer style={{width: width}}>
                <BrainImg src={BrainLogo} alt="brain-Loader" />
            </BrainImgContainer>
            <BrainLabel>{label}</BrainLabel>
        </BrainLoaderContainer>
    );
};

BrainLoader.propTypes = {
    label: propTypes.string,
    width: propTypes.string,
};

BrainLoader.defaultProps = {
    label: "",
    width: 'unset'
};

const StyledTextAreaContainer = styled.div`
    .styled-input {
        border: 1px solid black;
        width: 100%;
        height: 70px;
        padding-left: 12px;
    }
    .label {
        color: white;
    }
`;
export const StyledTextArea = ({ value, onChange, placeholder, label }) => {
    return (
        <StyledTextAreaContainer>
            <Label>{label}</Label>
            <textarea
                className="styled-input"
                type="text"
                value={value}
                onChange={onChange}
                placeholder={placeholder}
            />
        </StyledTextAreaContainer>
    );
};

StyledTextArea.propTypes = {
    placeholder: propTypes.string,
    value: propTypes.string,
    onChange: propTypes.func,
    label: propTypes.string,
};

StyledTextArea.defaultProps = {
    placeholder: 'placeholder',
    label: 'label',
    value: '',
    onChange: () => {},
};

const StyledSliderContainer = styled.div``;

const PrettoSlider = muiStyled(Slider)({
    color: '#fff',
    height: 8,
    '& .MuiSlider-track': {
        border: 'none',
    },
    '& .MuiSlider-thumb': {
        height: 24,
        width: 24,
        backgroundColor: '#fff',
        border: '2px solid currentColor',
        '&:focus, &:hover, &.Mui-active, &.Mui-focusVisible': {
            boxShadow: 'inherit',
        },
        '&:before': {
            display: 'none',
        },
    },
});

export const StyledSlider = ({ value, label, onChange }) => {
    const numberWithCommas = (x) => x?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    return (
        <StyledSliderContainer>
            <Label>{label}</Label>
            <Label>
                Budget: <span>${numberWithCommas(value)}</span>
            </Label>

            <PrettoSlider
                value={value}
                onChange={onChange}
                aria-label="Default"
                valueLabelDisplay="auto"
                color="secondary"
                step={100}
                min={100}
                max={50000}
            />
        </StyledSliderContainer>
    );
};

StyledSlider.propTypes = {
    value: propTypes.number,
    onChange: propTypes.func,
    label: propTypes.string,
};

StyledSlider.defaultProps = {
    label: 'label',
    value: 1,
    onChange: () => {},
};

const StyledCheckboxesContainer = styled.div`
    color: white;
`;
export const StyledCheckboxes = ({ label, options }) => {
    return (
        <StyledCheckboxesContainer>
            <FormControl>
                <Label>{label}</Label>
                <div>
                    {options.map((item, idx) => {
                        return (
                            <FormControlLabel
                                key={idx + 1}
                                control={
                                    <Checkbox
                                        key={idx + 1}
                                        checked={item.checked}
                                        onChange={item.onChange}
                                        inputProps={{ 'aria-label': 'controlled' }}
                                        sx={{
                                            color: 'white',
                                            '&.Mui-checked': {
                                                color: 'white',
                                            },
                                        }}
                                    />
                                }
                                label={item.label}
                            />
                        );
                    })}
                </div>
            </FormControl>
        </StyledCheckboxesContainer>
    );
};

StyledCheckboxes.propTypes = {
    // checked: propTypes.bool,
    // onChange: propTypes.func,
    label: propTypes.string,
    options: propTypes.array,
};

StyledCheckboxes.defaultProps = {
    label: 'label',
    // checked: false,
    // onChange: () => {},
    options: ['item1', 'item2'],
};

const StyledRadioButtonsContainer = styled.div`
    color: white;
`;
export const StyledRadioButtons = ({ label, value, onChange, options }) => {
    return (
        <StyledRadioButtonsContainer>
            <FormControl>
                <Label>{label}</Label>
                <RadioGroup
                    row
                    aria-labelledby="demo-row-radio-buttons-group-label"
                    name="row-radio-buttons-group"
                    value={value}
                    onChange={onChange}
                >
                    {options.map((item, idx) => {
                        return (
                            <FormControlLabel
                                key={idx + 1}
                                value={item.value}
                                control={
                                    <Radio
                                        style={{
                                            color: 'white',
                                        }}
                                    />
                                }
                                label={item.label}
                            />
                        );
                    })}
                </RadioGroup>
            </FormControl>
        </StyledRadioButtonsContainer>
    );
};

StyledRadioButtons.propTypes = {
    value: propTypes.string,
    onChange: propTypes.func,
    label: propTypes.string,
    options: propTypes.array,
};

StyledRadioButtons.defaultProps = {
    label: 'label',
    value: '',
    onChange: () => {},
    options: ['item1', 'item2'],
};

const StyledDropdownContainer = styled.div`
    .MuiFormControl-root {
        margin: 0px;
    }
`;

const BootstrapInput = muiStyled(InputBase)(({ theme }) => ({
    'label + &': {
        marginTop: theme.spacing(3),
    },
    '& .MuiInputBase-input': {
        borderRadius: 4,
        position: 'relative',
        backgroundColor: '#fafbf7',
        height: '27px',
        border: '1px solid #ced4da',
        fontSize: 16,
        padding: '10px 26px 10px 12px',
        transition: theme.transitions.create(['border-color', 'box-shadow']),
        // Use the system font instead of the default Roboto font.
        fontFamily: [
            '-apple-system',
            'BlinkMacSystemFont',
            '"Segoe UI"',
            'Roboto',
            '"Helvetica Neue"',
            'Arial',
            'sans-serif',
            '"Apple Color Emoji"',
            '"Segoe UI Emoji"',
            '"Segoe UI Symbol"',
        ].join(','),
        '&:focus': {
            borderRadius: 4,
            borderColor: '#80bdff',
            boxShadow: '0 0 0 0.2rem rgba(0,123,255,.25)',
        },
    },
}));

const StyledDropdown = ({ required, error, id, value, onChange, label, renderMenuItem }) => {
    return (
        <div>
            <StyledDropdownContainer>
                <FormControl required error sx={{ m: 1, minWidth: '100%' }} variant="outlined">
                    <Label id={id}>{label}</Label>
                    <Select
                        labelId={id}
                        id={id}
                        value={value}
                        onChange={onChange}
                        label={label}
                        input={<BootstrapInput />}
                    >
                        {renderMenuItem}
                    </Select>
                </FormControl>
            </StyledDropdownContainer>
        </div>
    );
};

StyledDropdown.propTypes = {
    required: propTypes.bool,
    error: propTypes.bool,
    id: propTypes.string,
    value: propTypes.string,
    onChange: propTypes.func,
    label: propTypes.string,
    renderMenuItem: propTypes.node,
};

StyledDropdown.defaultProps = {
    required: true,
    error: false,
    id: 'dropdown-id',
    value: 'value',
    onChange: () => {},
    label: 'dropdown',
    renderMenuItem: <div />,
};

export default StyledDropdown;
